// Stage 48 §17.1: full-page Connections view.
//
// Layout:
//   - Master switch panel (always visible)
//   - "+ New connection" button (disabled when master is off)
//   - Active connections list with per-row kebab menu
//   - Disabled connections (collapsed)
//   - New / Edit modals + Audit drawer slide-in

import { useEffect, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import {
  deleteConnection,
  listConnections,
  rotateConnection,
  updateConnection,
  type ConnectionListRow,
} from '../api/connections';
import { NewConnectionModal } from './NewConnectionModal';
import { EditConnectionModal } from './EditConnectionModal';
import { ConnectionAuditDrawer } from './ConnectionAuditDrawer';
import { RevealOnceField } from './RevealOnceField';

interface SettingsResponse {
  settings: Record<string, string>;
}

function formatTs(unix: number | null): string {
  if (!unix) return '—';
  return new Date(unix * 1000).toLocaleString();
}

function formatRelativeOrNever(unix: number | null): string {
  if (!unix) return 'never';
  const ageMs = Date.now() - unix * 1000;
  if (ageMs < 60_000) return 'just now';
  if (ageMs < 60 * 60_000) return `${Math.floor(ageMs / 60_000)}m ago`;
  if (ageMs < 24 * 60 * 60_000)
    return `${Math.floor(ageMs / (60 * 60_000))}h ago`;
  return new Date(unix * 1000).toLocaleDateString();
}

export function ConnectionsView() {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [maxActive, setMaxActive] = useState<number>(25);
  const [connections, setConnections] = useState<
    ConnectionListRow[] | null
  >(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<ConnectionListRow | null>(null);
  const [auditingId, setAuditingId] = useState<string | null>(null);
  const [showDisabled, setShowDisabled] = useState(false);
  const [rotated, setRotated] = useState<{
    plaintext: string;
    label: string;
  } | null>(null);

  async function loadAll(): Promise<void> {
    setErr(null);
    try {
      const [s, c] = await Promise.all([
        apiGet<SettingsResponse>('/api/settings'),
        listConnections().catch(() => ({ connections: [] })),
      ]);
      setEnabled(s.settings.connections_enabled === 'true');
      const cap = parseInt(
        s.settings.connections_global_max_active || '25',
        10,
      );
      setMaxActive(Number.isFinite(cap) ? cap : 25);
      setConnections(c.connections);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void loadAll();
  }, []);

  async function toggleMaster(next: boolean): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await apiJson('PUT', '/api/settings', {
        connections_enabled: next ? 'true' : 'false',
      });
      setEnabled(next);
      // Re-load: the list endpoint is admin-gated and works regardless
      // of the master flag, but the public-surface origin cache will
      // refresh server-side.
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function setRowEnabled(
    row: ConnectionListRow,
    next: boolean,
  ): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await updateConnection(row.id, { enabled: next });
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function rotate(row: ConnectionListRow): Promise<void> {
    if (
      !confirm(
        `Rotate token for "${row.label}"?\n\n` +
          'The current token will stop working immediately. The new ' +
          'token will be shown only once.',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const res = await rotateConnection(row.id);
      setRotated({ plaintext: res.plaintext_token, label: row.label });
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(row: ConnectionListRow): Promise<void> {
    if (
      !confirm(
        `Permanently delete connection "${row.label}"?\n\n` +
          'This cannot be undone. The audit log for this connection will ' +
          'also be deleted.',
      )
    ) {
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await deleteConnection(row.id);
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (enabled === null) {
    return (
      <div className="connections-page">
        <div className="muted">Loading…</div>
      </div>
    );
  }

  const active = (connections ?? []).filter((c) => c.enabled);
  const disabled = (connections ?? []).filter((c) => !c.enabled);

  return (
    <div className="connections-page">
      <header className="page-header">
        <div>
          <h1>Connections</h1>
          <p className="muted small">
            Mint per-app API tokens that give a remote application
            read-only access to your media gallery.
          </p>
        </div>
        <div className="row">
          <button
            className="btn-primary"
            disabled={!enabled || busy || active.length >= maxActive}
            onClick={() => setShowNew(true)}
            title={
              active.length >= maxActive
                ? `Max active reached (${maxActive}). Disable or revoke an existing connection first.`
                : 'New connection'
            }
          >
            + New connection
          </button>
        </div>
      </header>

      <div className="panel">
        <label className="voice-toggle-row">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => void toggleMaster(e.target.checked)}
            disabled={busy}
          />
          <span>Enable Connection system</span>
        </label>
        <p className="muted small">
          Off by default. While off, all <code>/api/public/v1/*</code>{' '}
          requests respond with <code>503</code> regardless of any tokens
          that may exist. Turn this on to expose the public surface.
        </p>
      </div>

      {!enabled ? (
        <div className="panel muted">
          The Connection system is disabled. Tokens listed below (if any)
          will not authorise any requests until the master switch above is
          turned on.
        </div>
      ) : null}

      <h2 className="section-heading">
        Active connections ({active.length} / {maxActive})
      </h2>
      {active.length === 0 ? (
        <div className="panel muted">No active connections yet.</div>
      ) : (
        <ul className="connection-list">
          {active.map((c) => (
            <ConnectionRow
              key={c.id}
              row={c}
              busy={busy}
              onEdit={() => setEditing(c)}
              onAudit={() => setAuditingId(c.id)}
              onDisable={() => void setRowEnabled(c, false)}
              onEnable={() => void setRowEnabled(c, true)}
              onRotate={() => void rotate(c)}
              onRevoke={() => void revoke(c)}
            />
          ))}
        </ul>
      )}

      {disabled.length > 0 ? (
        <>
          <h2
            className="section-heading section-heading-collapsible"
            onClick={() => setShowDisabled((v) => !v)}
          >
            {showDisabled ? '▾' : '▸'} Disabled connections (
            {disabled.length})
          </h2>
          {showDisabled ? (
            <ul className="connection-list">
              {disabled.map((c) => (
                <ConnectionRow
                  key={c.id}
                  row={c}
                  busy={busy}
                  onEdit={() => setEditing(c)}
                  onAudit={() => setAuditingId(c.id)}
                  onDisable={() => void setRowEnabled(c, false)}
                  onEnable={() => void setRowEnabled(c, true)}
                  onRotate={() => void rotate(c)}
                  onRevoke={() => void revoke(c)}
                />
              ))}
            </ul>
          ) : null}
        </>
      ) : null}

      {err ? <div className="status-missing">Error: {err}</div> : null}

      {showNew ? (
        <NewConnectionModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            void loadAll();
          }}
        />
      ) : null}

      {editing ? (
        <EditConnectionModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void loadAll();
          }}
        />
      ) : null}

      {auditingId ? (
        <ConnectionAuditDrawer
          connectionId={auditingId}
          connectionLabel={
            (connections ?? []).find((c) => c.id === auditingId)?.label ??
            ''
          }
          onClose={() => setAuditingId(null)}
        />
      ) : null}

      {rotated ? (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(e) => {
            if (e.target === e.currentTarget) setRotated(null);
          }}
        >
          <div className="modal modal-settings">
            <header className="modal-header">
              <h2>Rotated: save new token</h2>
              <button
                className="modal-close"
                onClick={() => setRotated(null)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <section className="modal-body">
              <div className="panel">
                <p className="muted small">
                  Connection: <strong>{rotated.label}</strong>
                </p>
                <RevealOnceField
                  value={rotated.plaintext}
                  label="New plaintext token"
                />
              </div>
            </section>
            <footer className="modal-footer">
              <button
                className="btn-primary"
                onClick={() => setRotated(null)}
              >
                Close
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface RowProps {
  row: ConnectionListRow;
  busy: boolean;
  onEdit: () => void;
  onAudit: () => void;
  onDisable: () => void;
  onEnable: () => void;
  onRotate: () => void;
  onRevoke: () => void;
}

function ConnectionRow({
  row,
  busy,
  onEdit,
  onAudit,
  onDisable,
  onEnable,
  onRotate,
  onRevoke,
}: RowProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const expired =
    row.expires_at != null &&
    row.expires_at < Math.floor(Date.now() / 1000);
  return (
    <li
      className={`connection-row ${row.enabled ? '' : 'connection-row-disabled'}`}
    >
      <div className="connection-row-main">
        <div className="connection-row-title">
          <strong>{row.label}</strong>
          {row.enabled ? (
            <span className="badge badge-ok">enabled</span>
          ) : (
            <span className="badge badge-off">DISABLED</span>
          )}
          {expired ? (
            <span className="badge badge-warn">expired</span>
          ) : null}
        </div>
        <div className="muted small">
          <code>{row.token_prefix}…</code>
          {' · '}
          {row.scopes.join(', ')}
          {' · '}
          <code>{row.allowed_origin}</code>
        </div>
        <div className="muted small">
          {row.rate_limit_per_minute}/min
          {' · '}
          last used {formatRelativeOrNever(row.last_used_at)}
          {' · '}
          {row.expires_at
            ? `expires ${formatTs(row.expires_at)}`
            : 'no expiry'}
          {row.stats
            ? ` · ${row.stats.total_ok} 2xx · ${row.stats.total_errors} errors`
            : ''}
        </div>
        {row.notes ? (
          <div className="muted small connection-row-notes">{row.notes}</div>
        ) : null}
      </div>
      <div className="connection-row-actions">
        <button
          type="button"
          className="kebab-button"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          disabled={busy}
        >
          ⋯
        </button>
        {menuOpen ? (
          <ul role="menu" className="kebab-menu">
            <li>
              <button
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onEdit();
                }}
              >
                Edit
              </button>
            </li>
            <li>
              <button
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onAudit();
                }}
              >
                View audit log
              </button>
            </li>
            <li>
              <button
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  onRotate();
                }}
              >
                Rotate token
              </button>
            </li>
            <li>
              {row.enabled ? (
                <button
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onDisable();
                  }}
                >
                  Disable
                </button>
              ) : (
                <button
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEnable();
                  }}
                >
                  Enable
                </button>
              )}
            </li>
            <li>
              <button
                role="menuitem"
                className="kebab-menu-danger"
                onClick={() => {
                  setMenuOpen(false);
                  onRevoke();
                }}
              >
                Revoke (delete)
              </button>
            </li>
          </ul>
        ) : null}
      </div>
    </li>
  );
}
